import React, { useEffect, useRef, useState } from 'react';
import Section from './Section';
import ServiceStatusIcons from './ServiceStatusIcons';
import { Patient, PatientActivityStatus, getPatientActivityStatus } from '../services/patientsService';
import { useIsMobile } from '../hooks/useIsMobile';
import { ClockIcon, DeviceIcon, PencilIcon, NoteDocIcon, DotsVerticalIcon, KeyIcon, MailIcon, TrashIcon } from '../assets/icons';

interface PatientProfileHeaderProps {
  patient: Patient;
  onEditData: () => void;
  onChangePassword: () => void;
  onSendTemplateEmail: () => void;
  onDelete: () => void;
  onOpenNotes?: () => void;
}

const ACTIVITY_STYLES: Record<PatientActivityStatus, { dot: string; bg: string; color: string; border: string }> = {
  active: { dot: '#10b981', bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
  inactive: { dot: '#f59e0b', bg: '#fffbeb', color: '#92400e', border: '#fde68a' },
  never: { dot: '#ef4444', bg: '#fef2f2', color: '#991b1b', border: '#fecaca' },
};

const MenuItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick: () => void;
}> = ({ icon, label, danger, onClick }) => {
  const [hover, setHover] = useState(false);
  const color = danger ? '#dc2626' : '#374151';
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '9px 12px',
        background: hover ? (danger ? '#fef2f2' : '#f3f4f6') : 'transparent',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 500,
        color,
        textAlign: 'left',
      }}
    >
      <span style={{ display: 'inline-flex', color: danger ? '#dc2626' : '#6b7280' }}>{icon}</span>
      {label}
    </button>
  );
};

// Compact, full-width identity bar: avatar, name, contact, activity + service
// status, and the primary edit action. Secondary/destructive actions live in
// the "⋯" menu. Detailed fields live in the "Perfil" tab.
const PatientProfileHeader: React.FC<PatientProfileHeaderProps> = ({ patient, onEditData, onChangePassword, onSendTemplateEmail, onDelete, onOpenNotes }) => {
  const isMobile = useIsMobile();
  const activity = getPatientActivityStatus(patient);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the "⋯" menu on outside click or Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const initials = `${(patient.firstName || '').charAt(0)}${(patient.lastName || '').charAt(0)}`.toUpperCase();

  const lastActivity = patient.lastActivityAt
    ? new Date(patient.lastActivityAt).toLocaleDateString('es-ES', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : 'Nunca';

  const activityStyle = ACTIVITY_STYLES[activity];
  const activityLabel = (() => {
    if (activity === 'never') return 'Nunca ha accedido';
    if (activity === 'inactive') {
      const date = patient.lastActivityAt
        ? new Date(patient.lastActivityAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
        : null;
      return `Inactivo${date ? ` · ${date}` : ''}`;
    }
    return 'Activo';
  })();

  const avatarSize = isMobile ? 56 : 64;
  const contactLine = [patient.email || '', patient.phone_number || '']
    .filter(Boolean)
    .join(' · ');

  return (
    <Section
      style={{
        marginBottom: 24,
        background: '#fff',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
        padding: isMobile ? 16 : '20px 24px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          flexWrap: isMobile ? undefined : 'wrap',
          gap: isMobile ? 14 : '14px 24px',
        }}
      >
        {/* Avatar + identity */}
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 14 : 18, flex: isMobile ? undefined : '1 1 320px', minWidth: 0 }}>
          {/* Avatar with activity dot */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{ width: avatarSize, height: avatarSize, borderRadius: '50%', overflow: 'hidden' }}>
              {patient.avatarUrl ? (
                <img
                  src={patient.avatarUrl}
                  alt={`${patient.firstName} ${patient.lastName}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, #4fd1c5 0%, #319795 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: Math.round(avatarSize * 0.38),
                    fontWeight: 700,
                    color: '#fff',
                    letterSpacing: 0.5,
                  }}
                >
                  {initials}
                </div>
              )}
            </div>
            <span
              aria-hidden
              style={{
                position: 'absolute',
                bottom: 1,
                right: 1,
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: activityStyle.dot,
                border: '2.5px solid #fff',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Identity */}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px 10px' }}>
              <span style={{ fontWeight: 700, fontSize: isMobile ? 18 : 20, color: '#111827', lineHeight: 1.3 }}>
                {patient.firstName || ''} {patient.lastName || ''}
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: activityStyle.bg,
                  color: activityStyle.color,
                  border: `1px solid ${activityStyle.border}`,
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: 999,
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: activityStyle.dot, flexShrink: 0 }} />
                {activityLabel}
              </span>
            </div>
            {contactLine && (
              <div style={{ color: '#6b7280', fontSize: 14, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {contactLine}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 7, fontSize: 12.5, color: '#6b7280' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ display: 'inline-flex', color: '#9ca3af' }}><ClockIcon /></span>
                Última actividad: <strong style={{ color: '#374151', fontWeight: 600 }}>{lastActivity}</strong>
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ display: 'inline-flex', color: '#9ca3af' }}><DeviceIcon /></span>
                App: <strong style={{ color: '#374151', fontWeight: 600 }}>{patient.app_version || '—'}</strong>
              </span>
            </div>
          </div>
        </div>

        {isMobile && <div style={{ height: 1, background: '#f3f4f6' }} />}

        {/* Service statuses + actions */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 12 : 16,
            justifyContent: isMobile ? 'space-between' : 'flex-end',
            flexShrink: 0,
            marginLeft: isMobile ? 0 : 'auto',
          }}
        >
          {patient.serviceStatus && (
            <ServiceStatusIcons
              status={patient.serviceStatus}
              weeklyDigestEnabled={patient.weeklyDigestEnabled ?? true}
              size={26}
              gap={8}
            />
          )}
          {!isMobile && patient.serviceStatus && (
            <div style={{ width: 1, height: 24, background: '#e5e7eb' }} />
          )}
          <div ref={menuRef} style={{ display: 'flex', gap: 8, position: 'relative' }}>
            <button
              onClick={onEditData}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                background: '#38b2ac',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '9px 18px',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: 14,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#319795'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#38b2ac'; }}
            >
              <PencilIcon />
              Editar datos
            </button>
            {onOpenNotes && (
              <button
                onClick={onOpenNotes}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  background: 'transparent',
                  color: '#0f766e',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: '9px 18px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f0fdfa'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <NoteDocIcon size={15} strokeWidth={2} />
                Notas
              </button>
            )}
            <button
              aria-label="Más acciones"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(open => !open)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                height: 38,
                background: menuOpen ? '#f3f4f6' : 'transparent',
                color: '#6b7280',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = menuOpen ? '#f3f4f6' : 'transparent'; }}
            >
              <DotsVerticalIcon />
            </button>
            {menuOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(16, 24, 40, 0.12)',
                  minWidth: 210,
                  padding: 4,
                  zIndex: 100,
                }}
              >
                <MenuItem
                  icon={<KeyIcon />}
                  label="Cambiar contraseña"
                  onClick={() => { setMenuOpen(false); onChangePassword(); }}
                />
                <MenuItem
                  icon={<MailIcon />}
                  label="Enviar email desde plantilla"
                  onClick={() => { setMenuOpen(false); onSendTemplateEmail(); }}
                />
                <MenuItem
                  icon={<TrashIcon />}
                  label="Eliminar paciente"
                  danger
                  onClick={() => { setMenuOpen(false); onDelete(); }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};

export default PatientProfileHeader;
